import { Link } from '@tanstack/react-router'
import { useMemo } from 'react'
import {
  useSequencerContainers,
  type ContainerListEntry,
} from '@/hooks/useSequencerProjects'
import { timeAgo } from '@/lib/format'
import { HomeWidget } from '../HomeWidget'

/**
 * Home widget — quick-open the most recently-updated sequencer
 * project. Mirrors the shape of `RecentLayouts` / `MidiStatus` so it
 * drops into the 3-column widget row cleanly. Self-hides when the
 * user has no projects yet.
 */
export function LatestProject() {
  const { data } = useSequencerContainers()
  const latest = useMemo(() => {
    if (!Array.isArray(data)) return null
    const sorted = [...data].sort(
      (a: ContainerListEntry, b: ContainerListEntry) =>
        new Date(b.updatedAt ?? b.createdAt ?? 0).getTime() -
        new Date(a.updatedAt ?? a.createdAt ?? 0).getTime(),
    )
    return sorted[0] ?? null
  }, [data])

  if (!latest) return null

  const totalPatterns = Object.values(latest.bankCounts ?? {}).reduce(
    (acc, n) => acc + n,
    0,
  )
  const usedBanks = Object.values(latest.bankCounts ?? {}).filter(
    n => n > 0,
  ).length

  return (
    <HomeWidget
      title="Latest project"
      action={
        <Link
          to="/werkshop/projects"
          className="text-[10px] font-semibold uppercase tracking-[0.14em] text-accent hover:text-[color:var(--accent-hover)]"
        >
          See all →
        </Link>
      }
    >
      <Link
        to="/werkshop/projects/$id"
        params={{ id: latest.id }}
        className="flex flex-col gap-1 py-1.5 rounded-md hover:bg-surface-sunken px-2 -mx-2 transition-colors"
      >
        <div className="flex items-center justify-between gap-3">
          <span className="truncate text-content-primary text-sm font-medium">
            {latest.name}
          </span>
          {(latest.updatedAt || latest.createdAt) && (
            <span
              className="text-[11px] text-content-tertiary flex-shrink-0"
              title={new Date(
                latest.updatedAt ?? latest.createdAt ?? '',
              ).toLocaleString()}
            >
              {timeAgo(latest.updatedAt ?? latest.createdAt ?? '')}
            </span>
          )}
        </div>
        <div className="text-[11px] text-content-tertiary">
          {totalPatterns} pattern{totalPatterns === 1 ? '' : 's'} · {usedBanks}{' '}
          bank{usedBanks === 1 ? '' : 's'}
        </div>
      </Link>
    </HomeWidget>
  )
}
